<template>
    <div class="admin-activity-report p-2">
        <div class="admin-activity-header">
            <div>
                <router-link class="admin-activity-back" :to="{ name: 'home' }">
                    <feather type="arrow-left" />
                    Panel Admin
                </router-link>
                <h5 class="title mb-1">Rapport d’activités</h5>
                <p class="text-muted mb-0">Activité totale créée mois par mois, filtrable par module.</p>
            </div>
            <div class="admin-activity-actions">
                <select
                    v-model.number="activityMonths"
                    class="form-select form-select-sm admin-activity-months select-native mb-0"
                    @change="fetchActivity">
                    <option :value="6">6 mois</option>
                    <option :value="12">12 mois</option>
                    <option :value="24">24 mois</option>
                    <option :value="36">36 mois</option>
                </select>
                <button type="button" class="btn btn-sm btn-outline-secondary btn-square" @click="fetchActivity">
                    <feather type="refresh-cw" />
                </button>
            </div>
        </div>

        <div class="admin-activity-summary">
            <div class="admin-activity-summary-card">
                <span>Total période</span>
                <strong>{{ activityTotal }}</strong>
            </div>
            <div v-for="module in selectedActivityModuleOptions" :key="module.key" class="admin-activity-summary-card">
                <span>{{ module.label }}</span>
                <strong>{{ moduleTotal(module.key) }}</strong>
            </div>
        </div>

        <section class="admin-activity-section">
            <div class="admin-activity-toolbar">
                <div class="admin-activity-modules">
                    <button
                        v-for="module in activityModules"
                        :key="module.key"
                        type="button"
                        class="admin-activity-module"
                        :class="{ active: selectedActivityModules.includes(module.key) }"
                        @click="toggleActivityModule(module.key)">
                        {{ module.label }}
                    </button>
                </div>
                <label class="form-label admin-activity-curve-toggle">
                    <input v-model="showCurveChart" type="checkbox" />
                    <span>Courbes</span>
                </label>
            </div>

            <LoaderComponent v-if="loading" />
            <div v-else-if="activityRows.length === 0" class="admin-activity-empty">Aucune activité sur la période.</div>
            <div v-else>
                <div class="admin-activity-chart-card">
                    <VChart class="admin-activity-chart" :option="activityChartOption" autoresize />
                </div>

                <div class="table-responsive">
                    <table class="table table-sm admin-activity-table">
                        <thead>
                            <tr>
                                <th>Mois</th>
                                <th>Total</th>
                                <th v-for="module in selectedActivityModuleOptions" :key="module.key">{{ module.label }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="month in activityRows" :key="month.month">
                                <td>
                                    <strong>{{ month.label }}</strong>
                                </td>
                                <td>
                                    <span class="badge text-bg-primary">{{ month.total }}</span>
                                </td>
                                <td v-for="module in selectedActivityModuleOptions" :key="module.key">
                                    {{ activityCountForModule(month, module.key) }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import axios from "axios";
import { use } from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import VChart from "vue-echarts";
import LoaderComponent from "../../components/LoaderComponent.vue";

use([CanvasRenderer, BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent]);

export default {
    name: "ActivityReport",
    data() {
        return {
            loading: false,
            activity: null,
            activityMonths: 12,
            activityModules: [
                { key: "reservations", label: "Réservations", color: "#157ec2" },
                { key: "orders", label: "Commandes", color: "#44a475" },
                { key: "gift_vouchers", label: "Bons cadeaux", color: "#e2a034" },
                { key: "campaigns", label: "Campagnes", color: "#8c63d9" },
                { key: "ordering_table", label: "Commande à table", color: "#d85f6a" },
            ],
            selectedActivityModules: ["reservations", "orders", "gift_vouchers", "campaigns", "ordering_table"],
            showCurveChart: false,
        };
    },
    computed: {
        selectedActivityModuleOptions() {
            return this.activityModules.filter((module) => this.selectedActivityModules.includes(module.key));
        },
        activityRows() {
            return [...(this.activity?.months || [])].reverse();
        },
        activityChartRows() {
            return this.activity?.months || [];
        },
        activityTotal() {
            return this.activityRows.reduce((total, month) => total + (month.total || 0), 0);
        },
        activityChartOption() {
            return {
                color: this.chartColors,
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: this.showCurveChart ? "line" : "shadow",
                    },
                    valueFormatter: (value) => `${value} activité(s)`,
                },
                legend: {
                    bottom: 0,
                    icon: "roundRect",
                    itemWidth: 12,
                    itemHeight: 8,
                    textStyle: {
                        color: "#435568",
                        fontSize: 12,
                    },
                },
                grid: {
                    top: 18,
                    right: 12,
                    bottom: 56,
                    left: 42,
                },
                xAxis: {
                    type: "category",
                    data: this.activityChartRows.map((month) => this.shortMonthLabel(month.label)),
                    axisTick: {
                        alignWithLabel: true,
                    },
                    axisLabel: {
                        color: "#627184",
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#d9e4ec",
                        },
                    },
                },
                yAxis: {
                    type: "value",
                    minInterval: 1,
                    axisLabel: {
                        color: "#627184",
                    },
                    splitLine: {
                        lineStyle: {
                            color: "#edf3f7",
                        },
                    },
                },
                series: this.activityChartSeries,
            };
        },
        chartColors() {
            return this.selectedActivityModuleOptions.map((module) => module.color);
        },
        activityChartSeries() {
            return this.selectedActivityModuleOptions.map((module) => {
                if (this.showCurveChart) {
                    return {
                        name: module.label,
                        type: "line",
                        smooth: true,
                        symbolSize: 7,
                        lineStyle: {
                            width: 3,
                            color: module.color,
                        },
                        itemStyle: {
                            color: module.color,
                        },
                        emphasis: {
                            focus: "series",
                        },
                        data: this.activityChartRows.map((month) => this.activityCountForModule(month, module.key)),
                    };
                }

                return {
                    name: module.label,
                    type: "bar",
                    stack: "activity",
                    barMaxWidth: 44,
                    emphasis: {
                        focus: "series",
                    },
                    data: this.activityChartRows.map((month) => this.activityCountForModule(month, module.key)),
                };
            });
        },
    },
    methods: {
        toggleActivityModule(module) {
            if (this.selectedActivityModules.includes(module)) {
                if (this.selectedActivityModules.length === 1) {
                    return;
                }

                this.selectedActivityModules = this.selectedActivityModules.filter((selectedModule) => selectedModule !== module);
            } else {
                this.selectedActivityModules = [...this.selectedActivityModules, module];
            }

            this.fetchActivity();
        },
        activityCountForModule(month, module) {
            return month.modules?.[module] || 0;
        },
        moduleTotal(module) {
            return this.activityRows.reduce((total, month) => total + this.activityCountForModule(month, module), 0);
        },
        shortMonthLabel(label) {
            if (!label) {
                return "";
            }

            const [month, year] = label.split(" ");

            return `${month.substring(0, 3)}. ${year}`;
        },
        fetchActivity() {
            this.loading = true;

            axios
                .get(`/api/admin/activity?months=${this.activityMonths}&modules=${this.selectedActivityModules.join(",")}`)
                .then((response) => {
                    this.activity = response.data.data;
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
    components: {
        LoaderComponent,
        VChart,
    },
    created() {
        this.fetchActivity();
    },
};
</script>

<style>
.admin-activity-report {
    width: 100%;
    max-width: 1180px;
    margin-right: auto;
    margin-left: auto;
}

.admin-activity-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 8px 2px 2px;
    margin-bottom: 16px;
}

.admin-activity-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    color: #607287;
    font-size: 0.82rem;
    font-weight: 600;
}

.admin-activity-back .feather {
    width: 15px;
    height: 15px;
}

.admin-activity-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-top: 30px;
}

.admin-activity-months {
    width: 120px;
}

.admin-activity-summary {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 12px;
    margin-bottom: 16px;
}

.admin-activity-summary-card {
    padding: 14px 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.admin-activity-summary-card span {
    display: block;
    color: #627184;
    font-size: 0.74rem;
    font-weight: 700;
    text-transform: uppercase;
}

.admin-activity-summary-card strong {
    display: block;
    margin-top: 6px;
    color: #182b3c;
    font-size: 1.25rem;
    line-height: 1.1;
}

.admin-activity-section {
    padding: 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.admin-activity-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.admin-activity-modules {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.admin-activity-module {
    padding: 6px 10px;
    border: 1px solid #d9e4ec;
    border-radius: 16px;
    background: #fff;
    color: #435568;
    font-size: 0.82rem;
    font-weight: 700;
}

.admin-activity-module.active {
    border-color: #157ec2;
    background: #eef7fc;
    color: #157ec2;
}

.admin-activity-curve-toggle {
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    margin-bottom: 0;
    padding: 6px 10px;
    border: 1px solid #d9e4ec;
    border-radius: 16px;
    background: #fff;
    color: #435568;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 700;
}

.admin-activity-curve-toggle input {
    margin: 0;
}

.admin-activity-empty {
    padding: 18px;
    border: 1px dashed #ced8e1;
    border-radius: 6px;
    color: #627184;
    text-align: center;
}

.admin-activity-chart-card {
    height: 360px;
    margin-bottom: 16px;
    padding: 12px;
    border: 1px solid #e2ebf1;
    border-radius: 6px;
    background: #fbfdff;
}

.admin-activity-chart {
    width: 100%;
    height: 100%;
}

.admin-activity-table {
    margin-bottom: 0;
}

.admin-activity-table th,
.admin-activity-table td {
    white-space: nowrap;
}

.admin-activity-table thead th {
    border-top: 0;
    background: #f4f8fb;
    color: #435568;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
}

.admin-activity-table tbody td {
    vertical-align: middle;
}

@media (max-width: 991.98px) {
    .admin-activity-header {
        flex-direction: column;
    }

    .admin-activity-actions {
        padding-top: 0;
    }

    .admin-activity-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .admin-activity-toolbar {
        flex-direction: column;
    }
}

@media (max-width: 575.98px) {
    .admin-activity-summary {
        grid-template-columns: 1fr;
    }
}
</style>
